<template>
	<div class="p-p-2 content">
		<DocumentClassList
			:loading="loading"
			:documentClasses="documentClasses"
			:isAdministrationPathOpen="isAdministrationPathOpen"
			@openEntryDialog="openEntryDialog"
			@openDeleteDialog="openDeleteDialog"
			@onRowSelect="onRowSelect"
		/>
	</div>
	<!-- Lösch-Dialog -->
	<DialogDelete v-if="isAdministrationPathOpen" :showDialog="deleteDialog" @onClose="deleteDialog = false" @onConfirm="deleteEntry()" />
</template>

<script lang="ts">
import {defineComponent, onMounted, reactive, ref} from 'vue';
import {useI18n} from "vue-i18n";
import DocumentClassList from "@/apps/administration/components/documentclasses/DocumentClassList.vue";
import DialogDelete from "@/components/DialogDelete.vue";
import {ClientManager} from "@/singletons/ClientManager";
import {DocumentClass, ErrorDto} from "@dex/squeeze-client-ts";
import router from "@/router";
import {ToastManager} from "@/util/ToastManager";
import {useToast} from "primevue/usetoast";
import {useSqueezeStore} from "@/apps/squeeze/store";

export default defineComponent({
	name: 'DocumentClassListView',
	components: {
		DocumentClassList,
		DialogDelete,
	},
	setup() {
		const {t} = useI18n();
		const toast = useToast();

		/** Current Vuex-Store */
		const store = useSqueezeStore();

		/** Service for Document-Classes */
		const documentClassService = ClientManager.getInstance().squeeze.documentClass;

		/** Name of the persistent document class setting that backs the "LLM active" column */
		const LLM_EXTRACTION_SETTING = "LLMExtraction";

		/** Is the page currently loading its data? */
		const loading = ref<boolean>(false);

		/** All Document-Classes (llmActive is lazily loaded from the LLMExtraction setting) */
		const documentClasses = ref<(DocumentClass & { llmActive?: boolean })[]>([]);

		/** A single item (used for insert/edit/delete */
		const rowData = reactive<DocumentClass>({name: "", description: ""});

		/** Show delete dialog? */
		const deleteDialog = ref<boolean>(false);

		/** Show edit-Dialog? */
		const editDialog = ref<boolean>(false);

		/** Message to be shown on screen in case of information/error */
		const message = ref<string>('');

		/** Message-Type to be shown: success, info, warn, error */
		const messageType = ref<string>('error');

		/** Text of the header in Entry-Dialog */
		const headerText = ref<string>('');

		/** Is the Administration Path open? */
		const isAdministrationPathOpen = ref<boolean>(false);

		/**
		 * Opens the Delete-Dialog
		 * @param {DocumentClass} row The row to delete
		 */
		const openDeleteDialog = (row: DocumentClass) => {
			Object.assign(rowData, row);
			deleteDialog.value = true;
		}

		/**
		 * Lazily loads the LLMExtraction setting for each document class to fill the "LLM active" column.
		 * Only runs when the FeatureFlag is active (one settings request per row).
		 */
		const loadLlmSettings = () => {
			if (!store.state.featureSet.llmExtraction) {
				return;
			}

			documentClasses.value.forEach(documentClass => {
				if (!documentClass.id) {
					return;
				}

				documentClassService.getDocumentClassSettings(documentClass.id)
					.then(settings => {
						const setting = (settings || []).find(s => s.settingName === LLM_EXTRACTION_SETTING);
						documentClass.llmActive = setting?.value === 'true';
					})
					.catch(() => {
						documentClass.llmActive = false;
					});
			});
		}

		/**
		 * Gets the Document-Classes
		 */
		const getDocumentClasses = async (showLoading: boolean) => {
			if (showLoading) {
				loading.value = true
			}

			try {
				documentClasses.value = await documentClassService.getAllDocumentClasses();
				loadLlmSettings();
			} catch (response: any) {
				const err = await response.json();
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
			} finally {
				loading.value = false;
			}
		}

		/** Deletes an entry */
		const deleteEntry = () => {
			if (!rowData) {
				return;
			}
			loading.value = true;
			const id = rowData.id;
			documentClassService.deleteDocumentClassById(id as number).then(() => {
				deleteDialog.value = false;
				getDocumentClasses(false); // Reload View
			}).catch(response => response.json().then ((err: ErrorDto) => {
				ToastManager.showError(toast, t('Squeeze.General.Error'), t('Squeeze.General.Error') + ": " + err.message);
			})).finally(() => {
				loading.value = false;
			})
		}

		/**
		 * Is triggered when a row in the table is clicked
		 * @param {number} documentClassId ID of the Document-Class that has been clicked
		 */
		const onRowSelect = (documentClassId: number) => {
			if(isAdministrationPathOpen.value) {
				router.push({ name: 'DocumentClassSubView', params: { documentClassId: documentClassId}})
			} else {
				router.push({name: "DocumentListValidation", params: {documentClassId: documentClassId}});
			}
		}

		/** Opens the DocumentClassSubView-Component */
		const openEntryDialog = () => {
			router.push({ name: 'DocumentClassSubView', params: { documentClassId: 0}});
		}

		onMounted(() => {
			getDocumentClasses(true);
			isAdministrationPathOpen.value = router.currentRoute.value.fullPath === '/app/administration/documentclasses';
		})

		return {
			t,
			toast,
			loading,
			documentClasses,
			rowData,
			deleteDialog,
			editDialog,
			message,
			messageType,
			headerText,
			isAdministrationPathOpen,
			openDeleteDialog,
			getDocumentClasses,
			deleteEntry,
			onRowSelect,
			openEntryDialog,
		};
	},
});

</script>

<style scoped>
</style>
